import { CashApyDisclosure, CashBackDisclosure } from '@fruitful/compliance';
import { formatExecutionTime } from '@fruitful/fruitils';
import { type DepositAccount } from '@fruitful/types';
import { type Href, Link } from 'expo-router';
import * as React from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    FootnoteRegular,
    LabelEmphasized,
    LabelRegular,
    type TextProps,
    XStack,
    YStack,
} from 'smoothie';

import { formatMaskedValue } from '../../common/utils/bankingUtils';
import { useGetApexAccounts } from '../../investment/hooks/useGetApexAccounts';
import { captureWarning } from '../../logger/Logger';
import { type Disclosure, DisclosureEvent } from '../types/BankAccount';

interface BaseBankingPolicyDisclosuresProps {
    acknowledgementText: string;
    policies: PolicyType[];
    disclosures: Record<PolicyType, PolicyDisclosure>;
    onPress?: () => void;
    textSize?: 'medium' | 'small';
}

interface BankingPolicyDisclosuresProps extends Omit<
    BaseBankingPolicyDisclosuresProps,
    'disclosures'
> {}

interface PolicyDisclosure {
    description: string;
    /**
     * Enables us to map Fruitful's published disclosures to the one or more {@link DisclosureType}s defined by Synctera
     */
    syncteraDisclosures: DisclosureType[];
    url: `${string}:${string}`;
    version: string;
}

export enum PolicyType {
    E_SIGN_AGREEMENT = 'E_SIGN_AGREEMENT',
    TERMS_OF_SERVICE = 'TERMS_OF_SERVICE',
    MEMBERSHIP_AGREEMENT = 'MEMBERSHIP_AGREEMENT',
    PREMIUM_TRIAL_MEMBERSHIP_TERMS = 'PREMIUM_TRIAL_MEMBERSHIP_TERMS',
    ACH_AUTHORIZATION = 'ACH_AUTHORIZATION',
    PRIVACY_POLICY = 'PRIVACY_POLICY',
    DEPOSIT_ACCOUNT_AGREEMENT = 'DEPOSIT_ACCOUNT_AGREEMENT',
    LEGACY_REG_DD = 'LEGACY_REG_DD',
    FORM_CRS = 'FORM_CRS',
    LEGACY_EMIGRANT_PRIVACY_POLICY = 'LEGACY_EMIGRANT_PRIVACY_POLICY',
    FORM_ADV_PART_2_BROCHURE = 'FORM_ADV_PART_2_BROCHURE',
    // APEX_CUSTODIAL_AGREEMENT = 'APEX_CUSTODIAL_AGREEMENT',
    CARD_AGREEMENT = 'CARD_AGREEMENT',
    SECURITY_AGREEMENT = 'SECURITY_AGREEMENT',
    // AUTO_PAY_AGREEMENT = 'AUTO_PAY_AGREEMENT',
    CARD_REWARDS_TERMS = 'CARD_REWARDS_TERMS',
    EMIGRANT_PRIVACY_NOTICE = 'EMIGRANT_PRIVACY_NOTICE',
    FRUITFUL_PRIVACY_NOTICE = 'FRUITFUL_PRIVACY_NOTICE',
    INVESTMENT_MANAGEMENT_AGREEMENT = 'INVESTMENT_MANAGEMENT_AGREEMENT',
    APEX_PRIVACY_NOTICE = 'APEX_PRIVACY_NOTICE',
    APEX_DISCLOSURES = 'APEX_DISCLOSURES',
    APEX_FORM_CRS = 'APEX_FORM_CRS',
}

export const HYS_ACCOUNT_OPEN_POLICY_DISCLOSURES: PolicyType[] = [
    PolicyType.E_SIGN_AGREEMENT,
    PolicyType.ACH_AUTHORIZATION,
    PolicyType.PRIVACY_POLICY,
    PolicyType.DEPOSIT_ACCOUNT_AGREEMENT,
    PolicyType.LEGACY_REG_DD,
    PolicyType.LEGACY_EMIGRANT_PRIVACY_POLICY,
];

export const FRUITFUL_CASH_ACCOUNT_OPEN_POLICY_DISCLOSURES: PolicyType[] = [
    PolicyType.E_SIGN_AGREEMENT,
    PolicyType.ACH_AUTHORIZATION,
    PolicyType.DEPOSIT_ACCOUNT_AGREEMENT,
    PolicyType.FRUITFUL_PRIVACY_NOTICE,
    PolicyType.EMIGRANT_PRIVACY_NOTICE,
];

// Combined disclosures for batch creation of cash accounts
export const FRUITFUL_BATCH_OPEN_CASH_POLICY_DISCLOSURES: PolicyType[] = [
    PolicyType.E_SIGN_AGREEMENT,
    PolicyType.DEPOSIT_ACCOUNT_AGREEMENT,
    PolicyType.ACH_AUTHORIZATION,
    PolicyType.EMIGRANT_PRIVACY_NOTICE,
    PolicyType.FRUITFUL_PRIVACY_NOTICE,
];

// Combined disclosures for batch creation of card accounts
// This is assumes/requires that the batch card disclosures have been viewed before these
export const FRUITFUL_BATCH_OPEN_CARD_POLICY_DISCLOSURES: PolicyType[] = [
    PolicyType.E_SIGN_AGREEMENT,
    PolicyType.CARD_AGREEMENT,
    PolicyType.CARD_REWARDS_TERMS,
    PolicyType.SECURITY_AGREEMENT,
];

// Combined disclosures for batch creation of investment accounts
export const FRUITFUL_BATCH_OPEN_APEX_ACCOUNTS_POLICY_DISCLOSURES: PolicyType[] =
    [
        PolicyType.INVESTMENT_MANAGEMENT_AGREEMENT,
        PolicyType.FORM_ADV_PART_2_BROCHURE,
        PolicyType.APEX_PRIVACY_NOTICE,
        PolicyType.APEX_FORM_CRS,
        PolicyType.APEX_DISCLOSURES,
    ];

export const FRUITFUL_CARD_ACCOUNT_OPEN_POLICY_DISCLOSURES: PolicyType[] = [
    PolicyType.E_SIGN_AGREEMENT, // Added per ENG-4367
    PolicyType.CARD_AGREEMENT,
    PolicyType.SECURITY_AGREEMENT,
];

/**
 * The types of Disclosures that we must send to Synctera, @see https://dev.synctera.com/reference/listdisclosures
 *
 * @privateRemarks
 * TODO: move this element of backend schema into a shared api-schema package and import
 * For now, this should be kept in sync with @see /apps/backend/src/modules/synctera-client/persons/dto/syncteraDisclosureDto.ts
 */
enum DisclosureType {
    ACH_AUTHORIZATION = 'ACH_AUTHORIZATION',
    CARDHOLDER_AGREEMENT = 'CARDHOLDER_AGREEMENT',
    E_SIGN = 'E_SIGN',
    KYC_DATA_COLLECTION = 'KYC_DATA_COLLECTION',
    PRIVACY_NOTICE = 'PRIVACY_NOTICE',
    REG_CC = 'REG_CC',
    REG_DD = 'REG_DD',
    REG_E = 'REG_E',
    TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
    SMARTCARD_ACCOUNT_AGREEMENT = 'SC_ACCOUNT_AGREEMENT',
    SMARTCARD_SECURITY_AGREEMENT = 'SC_SECURITY_AGREEMENT',
    SMARTCARD_AUTOPAYMENT = 'SC_AUTO_PAYMENT',
}

export const BANKING_GENERAL_DISCLOSURE_TEXT = `Fruitful is a financial technology company, not a bank. Deposit accounts provided by Emigrant Bank, Member FDIC. The Fruitful Card is issued by Emigrant Bank pursuant to license by Mastercard International Incorporated.`;

const BANKING_DISCLOSURE_SHORT_TEXT =
    'Banking services provided by Emigrant Bank, Member FDIC.';

export const GENERAL_DISCLOSURES_TEXT = `© Fruitful ${new Date().getFullYear()} - All rights reserved. "Fruitful" refers to Fruitful, Inc. and its separate, affiliated subsidiaries. Fruitful, Inc. is an investment adviser registered with the U.S. Securities & Exchange Commission. Learn more about Fruitful in our Form ADV Part 2 and Form CRS available at www.adviserinfo.sec.gov. All investing involves risk including the risk of losing money you invest, and past performance does not guarantee future results. Only members of Fruitful have access to products and services across the Fruitful affiliates and subsidiaries. Fruitful is not a bank. Deposit accounts provided by Emigrant Bank, Member FDIC. Funds in the bank accounts are insured for up to $250,000 per depositor, depending on the ownership category. Interest rates are variable and subject to change at any time.`;

const BANKING_DISCLOSURE_TEXT =
    'Deposit accounts provided by Emigrant Bank, Member FDIC. Funds in the bank accounts are insured for up to $250,000 per depositor, depending on the ownership category.';

export const INVESTMENT_NOT_FDIC_INSURED =
    'Investment products and services provided by Fruitful Advisory, LLC are NOT FDIC INSURED, NOT BANK GUARANTEED, and MAY LOSE VALUE.';

export const ONE_TIME_INTERNAL_TRANSFER_DISCLOSURE_TEXT =
    'Internal transfers between Fruitful accounts take place immediately.';

export const RECURRING_INTERNAL_TRANSFER_DISCLOSURE_TEXT =
    'Internal transfers between Fruitful accounts take place immediately once initiated.';

export const EXTERNAL_OUTGOING_TRANSFER_TIMEFRAME_DISCLOSURE_TEXT =
    'Outgoing transfers are generally available in 3-5 business days.';

export const EXTERNAL_INCOMING_TRANSFER_TIMELINE_DISCLOSURE_TEXT =
    'Incoming transfers are generally available in 3-5 business days.';

export const ONE_TIME_EXTERNAL_TRANSFER_DISCLOSURE_TEXT =
    'Transfers submitted prior to 4:00pm ET Monday - Friday will be initiated on the same day. Transfers submitted after 4:00pm ET or on weekends / holidays will be initiated on the next business day.';

export const STOP_PAYMENT_DISCLOSURE_TEXT =
    'When the NYSE closes early, transfers to investment accounts need to be submitted by 1:00pm ET to be processed the same day.\n\nStop payments must be received at least three (3) business days before the payment is scheduled to be made.';

export const DEFAULT_RECURRING_EXECUTION_TIME_24H = '06:30:00';
export const LEGACY_RECURRING_EXECUTION_TIME_24H = '11:00:00';

const getRecurringTransferDisclosureText = (executionTime: string) => {
    const time = formatExecutionTime(executionTime);
    return `Recurring transfers are initiated at approximately ${time} on the scheduled day. If a recurring transfer falls on a weekend or holiday, the transfer will be initiated on the previous day.`;
};

const TAX_DOCUMENT_DISCLOSURE_TEXT =
    'Tax documents are only available to the primary account holder. A 1099-INT document will exist when the interest earned across all accounts was $10 or more.';

const FRUITFUL_CARD_DISCLOSURE =
    'The Fruitful Card is issued by Emigrant Bank pursuant to license by Mastercard International Incorporated.';

export const FRUITFUL_FDIC_DISCLOSURE =
    'Fruitful is not an FDIC-insured depository institution. Banking services provided by Emigrant Bank, Member FDIC.';

export const INVESTMENT_PRODUCTS_FDIC_DISCLOSURE =
    'Investment Products are not insured by the FDIC; are not deposits; and may lose value.';

const APPLE_TRADEMARKS_TEXT =
    'Apple, the Apple logo, iPhone, and iPad are trademarks of Apple Inc., registered in the U.S. and other countries. Apple Pay, Touch ID and Apple Watch are trademarks of Apple Inc.';

const INVESTMENT_MANAGEMENT_AGREEMENT_LINK_PLACEHOLDER =
    '{InvestmentManagementAgreementLink}';
const INVESTMENT_MANAGEMENT_AGREEMENT_DISCLOSURE_TEXT = `Fruitful Advisory LLC has outlined the rights and responsibilities of our working relationship in its ${INVESTMENT_MANAGEMENT_AGREEMENT_LINK_PLACEHOLDER}.`;

const DISCLOSURE_ACKNOWLEDGEMENT_TEXT = (
    acknowledgementButtonLabel: string,
    termsLabel: string,
) =>
    `By clicking "${acknowledgementButtonLabel}", you acknowledge you have reviewed and accept the terms of ${termsLabel}`;

export const POLICY_DISCLOSURES: Record<PolicyType, PolicyDisclosure> = {
    [PolicyType.E_SIGN_AGREEMENT]: {
        description: 'E-Sign Agreement',
        syncteraDisclosures: [DisclosureType.E_SIGN],
        url: 'https://fruitful.com/legal/e-sign', // Redirects to https://d1pwidzl9kib4u.cloudfront.net/legal/e-sign-consent-disclosure.pdf
        version: '1.0',
    }, // banking, legacy banking
    [PolicyType.TERMS_OF_SERVICE]: {
        description: 'Terms of Service',
        // Intentionally empty as Synctera does not need to acknowledge our site's Terms of Service.
        // Instead Synctera cares about DisclosureType.TERMS_AND_CONDITIONS, which we have in our Deposit Account Agreement.
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/tos',
        version: '1.0',
    },
    [PolicyType.MEMBERSHIP_AGREEMENT]: {
        description: 'Membership Agreement',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/fruitful-membership-agreement',
        version: '1.0',
    },
    [PolicyType.PREMIUM_TRIAL_MEMBERSHIP_TERMS]: {
        description: 'Premium Trial Membership Terms',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/premium-trial',
        version: '1.0',
    },
    [PolicyType.ACH_AUTHORIZATION]: {
        description: 'ACH Authorization',
        syncteraDisclosures: [DisclosureType.ACH_AUTHORIZATION],
        url: 'https://fruitful.com/legal/ach-auth',
        version: '1.0',
    }, // banking, legacy banking
    [PolicyType.PRIVACY_POLICY]: {
        description: 'Privacy Policy',
        syncteraDisclosures: [DisclosureType.PRIVACY_NOTICE],
        url: 'https://fruitful.com/legal/privacy',
        version: '1.0',
    }, // banking, legacy banking
    [PolicyType.DEPOSIT_ACCOUNT_AGREEMENT]: {
        description: 'Deposit Account Agreement',
        syncteraDisclosures: [
            DisclosureType.TERMS_AND_CONDITIONS,
            DisclosureType.REG_CC,
            DisclosureType.REG_DD,
            DisclosureType.REG_E,
        ],
        url: 'https://fruitful.com/legal/deposit-account-agreement',
        version: '1.0',
    }, // Fruitful Deposit account agreement updated 2025-07-15 (both Savings and Cash), replaces /legal/cash-daa and /legal/daa
    [PolicyType.LEGACY_REG_DD]: {
        description: 'Reg DD',
        syncteraDisclosures: [DisclosureType.REG_DD],
        url: 'https://fruitful.com/legal/reg-dd',
        version: '1.0',
    }, // legacy banking (HYS only)
    [PolicyType.FORM_CRS]: {
        description: 'Form CRS',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/form-crs',
        version: '1.0',
    },
    [PolicyType.LEGACY_EMIGRANT_PRIVACY_POLICY]: {
        description: "Emigrant Bank's Privacy Policy",
        syncteraDisclosures: [],
        url: 'https://assets.fruitful.com/legal/emigrant-privacy-notice.pdf',
        version: 'n/a',
    }, // legacy banking (HYS only)
    [PolicyType.FORM_ADV_PART_2_BROCHURE]: {
        description: 'Form ADV & Client Relationship Summary',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/adv2',
        version: '1.0',
    },
    // [PolicyType.APEX_CUSTODIAL_AGREEMENT]: {
    //     description: 'Apex Custodial Agreement',
    //     syncteraDisclosures: [],
    //     url: 'https://fruitful.com/legal/apex',
    //     version: '1.0',
    // }, // not referenced as of 2024-10-09
    [PolicyType.CARD_AGREEMENT]: {
        description: 'Card Agreement',
        syncteraDisclosures: [DisclosureType.SMARTCARD_ACCOUNT_AGREEMENT],
        url: 'https://fruitful.com/legal/card-agreement',
        version: '1.0',
    },
    [PolicyType.SECURITY_AGREEMENT]: {
        description: 'Security Agreement',
        syncteraDisclosures: [DisclosureType.SMARTCARD_SECURITY_AGREEMENT],
        url: 'https://fruitful.com/legal/card-security-agreement',
        version: '1.0',
    },
    [PolicyType.CARD_REWARDS_TERMS]: {
        description: 'Fruitful Card Rewards Program Terms and Conditions',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/rewards-tc',
        version: '1.0',
    },
    [PolicyType.EMIGRANT_PRIVACY_NOTICE]: {
        description: "Emigrant Bank's Privacy Notice",
        syncteraDisclosures: [DisclosureType.PRIVACY_NOTICE],
        url: 'https://fruitful.com/legal/emigrant-privacy-notice',
        version: '1.0',
    },
    [PolicyType.FRUITFUL_PRIVACY_NOTICE]: {
        description: "Fruitful's Privacy Notice",
        syncteraDisclosures: [DisclosureType.PRIVACY_NOTICE],
        url: 'https://fruitful.com/legal/fruitful-privacy-notice',
        version: '1.0',
    },
    [PolicyType.INVESTMENT_MANAGEMENT_AGREEMENT]: {
        description: 'Investment Management Agreement',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/im-agree',
        version: '1.0',
    },
    [PolicyType.APEX_PRIVACY_NOTICE]: {
        description: "Apex Clearing's Privacy Notice",
        syncteraDisclosures: [],
        url: 'https://public-documents.apexapis.com/account-agreements/PRIU-UNI-20251123-EN.pdf',
        version: '20251123',
    },
    [PolicyType.APEX_DISCLOSURES]: {
        description: 'Apex Disclosures',
        syncteraDisclosures: [],
        url: 'https://apexfintechsolutions.com/legal/disclosures/',
        version: '1.0',
    },
    [PolicyType.APEX_FORM_CRS]: {
        description: "Apex's Form CRS",
        syncteraDisclosures: [],
        url: 'https://library.apexfintechsolutions.com/wp-content/uploads/2023/11/Form-CRS-02.01.2024.pdf',
        version: '1.0',
    },
};

// This is a duplicate of the POLICY_DISCLOSURES const above, but modified for
// the money map batch opening screen only.
export const POLICY_DISCLOSURES_BATCH_OPENING: Record<
    PolicyType,
    PolicyDisclosure
> = {
    [PolicyType.E_SIGN_AGREEMENT]: {
        description: "Fruitful's E-Sign Agreement",
        syncteraDisclosures: [DisclosureType.E_SIGN],
        url: 'https://fruitful.com/legal/e-sign', // Redirects to https://d1pwidzl9kib4u.cloudfront.net/legal/e-sign-consent-disclosure.pdf
        version: '1.0',
    },
    [PolicyType.TERMS_OF_SERVICE]: {
        description: 'Terms of Service',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/tos',
        version: '1.0',
    },
    [PolicyType.MEMBERSHIP_AGREEMENT]: {
        description: 'Membership Agreement',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/fruitful-membership-agreement',
        version: '1.0',
    },
    [PolicyType.PREMIUM_TRIAL_MEMBERSHIP_TERMS]: {
        description: 'Premium Trial Membership Terms',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/premium-trial',
        version: '1.0',
    },
    [PolicyType.ACH_AUTHORIZATION]: {
        description: 'ACH Authorization',
        syncteraDisclosures: [DisclosureType.ACH_AUTHORIZATION],
        url: 'https://fruitful.com/legal/ach-auth',
        version: '1.0',
    }, // banking, legacy banking
    [PolicyType.PRIVACY_POLICY]: {
        description: 'Privacy Policy',
        syncteraDisclosures: [DisclosureType.PRIVACY_NOTICE],
        url: 'https://fruitful.com/legal/privacy',
        version: '1.0',
    }, // banking, legacy banking
    [PolicyType.DEPOSIT_ACCOUNT_AGREEMENT]: {
        description: 'Fruitful Deposit Account Agreement',
        syncteraDisclosures: [
            DisclosureType.TERMS_AND_CONDITIONS,
            DisclosureType.REG_CC,
            DisclosureType.REG_DD,
            DisclosureType.REG_E,
        ],
        url: 'https://fruitful.com/legal/deposit-account-agreement',
        version: '1.0',
    }, // Fruitful Deposit Account Agreement (updated 2025-07-15)
    [PolicyType.LEGACY_REG_DD]: {
        description: 'Reg DD',
        syncteraDisclosures: [DisclosureType.REG_DD],
        url: 'https://fruitful.com/legal/reg-dd',
        version: '1.0',
    }, // legacy banking (HYS only)
    [PolicyType.FORM_CRS]: {
        description: 'Form CRS',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/form-crs',
        version: '1.0',
    },
    [PolicyType.LEGACY_EMIGRANT_PRIVACY_POLICY]: {
        description: "Emigrant Bank's Privacy Policy",
        syncteraDisclosures: [],
        url: 'https://assets.fruitful.com/legal/emigrant-privacy-notice.pdf',
        version: 'n/a',
    }, // legacy banking (HYS only)
    [PolicyType.CARD_AGREEMENT]: {
        description: 'Fruitful Secured Charge Card Agreement',
        syncteraDisclosures: [DisclosureType.SMARTCARD_ACCOUNT_AGREEMENT],
        url: 'https://fruitful.com/legal/card-agreement',
        version: '1.0',
    },
    [PolicyType.SECURITY_AGREEMENT]: {
        description: 'Security Agreement',
        syncteraDisclosures: [DisclosureType.SMARTCARD_SECURITY_AGREEMENT],
        url: 'https://fruitful.com/legal/card-security-agreement',
        version: '1.0',
    },
    [PolicyType.CARD_REWARDS_TERMS]: {
        description: 'Fruitful Card Rewards Program Terms and Conditions',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/rewards-tc',
        version: '1.0',
    },
    [PolicyType.EMIGRANT_PRIVACY_NOTICE]: {
        description: 'Emigrant Bank Privacy Notice',
        syncteraDisclosures: [DisclosureType.PRIVACY_NOTICE],
        url: 'https://fruitful.com/legal/emigrant-privacy-notice',
        version: '1.0',
    },
    [PolicyType.FRUITFUL_PRIVACY_NOTICE]: {
        description: 'Fruitful Privacy Notice',
        syncteraDisclosures: [DisclosureType.PRIVACY_NOTICE],
        url: 'https://fruitful.com/legal/fruitful-privacy-notice',
        version: '1.0',
    },
    [PolicyType.INVESTMENT_MANAGEMENT_AGREEMENT]: {
        description: 'Investment Management Agreement',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/im-agree',
        version: '1.0',
    },
    [PolicyType.APEX_PRIVACY_NOTICE]: {
        description: "Apex Clearing's Privacy Notice",
        syncteraDisclosures: [],
        url: 'https://public-documents.apexapis.com/account-agreements/PRIU-UNI-20251123-EN.pdf',
        version: '20251123',
    },
    [PolicyType.FORM_ADV_PART_2_BROCHURE]: {
        description: 'Form ADV & Client Relationship Summary',
        syncteraDisclosures: [],
        url: 'https://fruitful.com/legal/adv2',
        version: '20250328',
    },
    [PolicyType.APEX_DISCLOSURES]: {
        description: 'Apex Disclosures',
        syncteraDisclosures: [],
        url: 'https://apexfintechsolutions.com/legal/disclosures/',
        version: '1.0',
    },
    [PolicyType.APEX_FORM_CRS]: {
        description: "Apex's Form CRS",
        syncteraDisclosures: [],
        url: 'https://library.apexfintechsolutions.com/wp-content/uploads/2023/11/Form-CRS-02.01.2024.pdf',
        version: '1.0',
    },
};

export const getBankingDisclosures = ({
    withKyc,
    enableAutoPay,
    policyTypes,
}: {
    withKyc?: boolean;
    enableAutoPay?: boolean;
    policyTypes: PolicyType[];
}): Disclosure[] => {
    const eventType = DisclosureEvent.ACKNOWLEDGED;
    const version = '1.0'; // As of 2024-10-09 only the version string 1.0 is supported by Synctera
    const disclosures: Disclosure[] = [];

    if (withKyc) {
        disclosures.push({
            type: DisclosureType.KYC_DATA_COLLECTION,
            eventType,
            version,
            metadata: undefined,
        });
    }
    if (enableAutoPay) {
        disclosures.push({
            type: DisclosureType.SMARTCARD_AUTOPAYMENT,
            eventType,
            version,
            metadata: undefined,
        });
    }

    for (const policyType of policyTypes) {
        const policyDisclosures = POLICY_DISCLOSURES[policyType];
        for (const syncteraDisclosure of policyDisclosures.syncteraDisclosures) {
            disclosures.push({
                type: syncteraDisclosure,
                eventType,
                version,
                metadata: {
                    url: policyDisclosures.url,
                    description: policyDisclosures.description,
                },
            });
        }
    }

    return disclosures;
};

/** @deprecated - Only used on legacy banking screens, pre-Cash+Card */
export const BankDisclosureFootnote = () => {
    return (
        <LabelEmphasized color="$gray900" textAlign="center">
            {BANKING_DISCLOSURE_TEXT}
        </LabelEmphasized>
    );
};

export const BankGeneralDisclosureFootnote = ({
    color = '$gray900',
}: {
    color?: string;
}) => {
    return (
        <LabelRegular color={color}>
            {BANKING_GENERAL_DISCLOSURE_TEXT}
        </LabelRegular>
    );
};

export const BankDisclosureShortFootnote = ({
    color = '$gray900',
}: {
    color?: string;
}) => {
    return (
        <LabelEmphasized color={color} textAlign="center">
            {BANKING_DISCLOSURE_SHORT_TEXT}
        </LabelEmphasized>
    );
};

export const BankDisclosureCashCardFootnote = ({
    color = '$gray900',
}: {
    color?: string;
}) => {
    return (
        <LabelRegular color={color}>
            {BANKING_DISCLOSURE_SHORT_TEXT} {FRUITFUL_CARD_DISCLOSURE}
        </LabelRegular>
    );
};

export const GeneralDisclosuresFootnote = ({
    color = '$gray900',
}: {
    color?: string;
}) => {
    return (
        <LabelEmphasized color={color} textAlign="center">
            {GENERAL_DISCLOSURES_TEXT}
        </LabelEmphasized>
    );
};

export const AppleDisclosuresFootnote = ({
    color = '$gray900',
}: {
    color?: string;
}) => {
    return (
        <LabelEmphasized color={color} textAlign="center">
            {APPLE_TRADEMARKS_TEXT}
        </LabelEmphasized>
    );
};

export const FruitfulCardDisclosure = ({
    color = '$gray900',
}: {
    color?: string;
}) => {
    return (
        <BodySmallRegular color={color} textAlign="center">
            {FRUITFUL_CARD_DISCLOSURE}
        </BodySmallRegular>
    );
};

export const OneTimeInternalTransferDisclosureFootnote = (props: TextProps) => {
    return (
        <FootnoteRegular color="$gray1000" textAlign="center" {...props}>
            {ONE_TIME_INTERNAL_TRANSFER_DISCLOSURE_TEXT}
        </FootnoteRegular>
    );
};

export const RecurringInternalTransferDisclosureFootnote = ({
    executionTime,
    ...props
}: { executionTime: string } & TextProps) => {
    return (
        <FootnoteRegular color="$gray1000" textAlign="center" {...props}>
            {getRecurringTransferDisclosureText(executionTime)}
            {'\n'}
            {RECURRING_INTERNAL_TRANSFER_DISCLOSURE_TEXT}
        </FootnoteRegular>
    );
};

export const RecurringExternalTransferDisclosureFootnote = ({
    isIncoming,
    executionTime,
    ...props
}: {
    isIncoming?: boolean;
    executionTime: string;
} & TextProps) => {
    return (
        <FootnoteRegular color="$gray1000" textAlign="center" {...props}>
            {getRecurringTransferDisclosureText(executionTime)}{' '}
            {isIncoming
                ? `\n${EXTERNAL_INCOMING_TRANSFER_TIMELINE_DISCLOSURE_TEXT}`
                : ''}
        </FootnoteRegular>
    );
};

export const OneTimeExternalTransferDisclosureFootnote = ({
    isIncoming,
    ...props
}: {
    isIncoming?: boolean;
} & TextProps) => {
    return (
        <FootnoteRegular color="$gray1000" textAlign="center" {...props}>
            {ONE_TIME_EXTERNAL_TRANSFER_DISCLOSURE_TEXT}{' '}
            {isIncoming
                ? `\n${EXTERNAL_INCOMING_TRANSFER_TIMELINE_DISCLOSURE_TEXT}`
                : ''}
        </FootnoteRegular>
    );
};

export const ConfirmTransferDisclosure = (props: TextProps) => {
    return (
        <FootnoteRegular
            color="$gray1000"
            textAlign="center"
            marginBottom={16}
            marginTop={8}
            {...props}
        >
            By confirming below, I authorize Fruitful to{'\n'}initiate the
            transfer as stated above.
        </FootnoteRegular>
    );
};

export const TokenizedAccountNumberDisclosure = (
    props: TextProps & { account: DepositAccount },
) => {
    const { account, ...textProps } = props;
    const { bankName, accountNumberMasked, nonTokenizedAccountNumberMasked } =
        account;
    return (
        <FootnoteRegular color="$gray1000" {...textProps}>
            {`${bankName} uses tokenized account numbers and will display ${formatMaskedValue(accountNumberMasked)} for account ending in ${formatMaskedValue(nonTokenizedAccountNumberMasked)}.`}
        </FootnoteRegular>
    );
};

export const TaxDocumentDisclosureFootnote = (props: TextProps) => {
    return (
        <BodyMediumRegular {...props}>
            {TAX_DOCUMENT_DISCLOSURE_TEXT}
        </BodyMediumRegular>
    );
};

export const EBBankServicesDisclosure = (props: TextProps) => {
    return (
        <BodyMediumRegular
            color="$gray1000"
            textAlign="center"
            marginBottom={16}
            {...props}
        >
            Fruitful banking services are provided by{'\n'}Emigrant Bank, Member
            FDIC.
        </BodyMediumRegular>
    );
};

export const InvestmentManagementAgreementDisclosure = ({
    acknowledgementButtonLabel,
}: {
    acknowledgementButtonLabel?: string;
}) => {
    const disclosure =
        POLICY_DISCLOSURES[PolicyType.INVESTMENT_MANAGEMENT_AGREEMENT];

    const acknowledgementText =
        acknowledgementButtonLabel != null
            ? DISCLOSURE_ACKNOWLEDGEMENT_TEXT(
                  acknowledgementButtonLabel,
                  'this agreement',
              )
            : 'By signing up, you acknowledge you have reviewed and accept the terms of this agreement';

    return (
        <DisclosureTextWithLink
            text={`${INVESTMENT_MANAGEMENT_AGREEMENT_DISCLOSURE_TEXT} ${acknowledgementText}.`}
            linkPlaceholder={INVESTMENT_MANAGEMENT_AGREEMENT_LINK_PLACEHOLDER}
            linkUrl={disclosure.url}
            linkText={disclosure.description}
        />
    );
};

export const NewMemberAccountDisclosure = () => {
    const eSignAgreement = POLICY_DISCLOSURES[PolicyType.E_SIGN_AGREEMENT];
    const termsOfService = POLICY_DISCLOSURES[PolicyType.TERMS_OF_SERVICE];
    const privacyPolicy = POLICY_DISCLOSURES[PolicyType.PRIVACY_POLICY];

    return (
        <DisclosureTextWithLinks
            text={
                'By clicking "Create account", you acknowledge you have reviewed & accept the terms of Fruitful\'s {ESignAgreementLink}, {TermsOfServiceLink}, & {PrivacyPolicyLink}.'
            }
            links={[
                {
                    placeholder: '{ESignAgreementLink}',
                    url: eSignAgreement.url,
                    text: eSignAgreement.description,
                },
                {
                    placeholder: '{TermsOfServiceLink}',
                    url: termsOfService.url,
                    text: termsOfService.description,
                },
                {
                    placeholder: '{PrivacyPolicyLink}',
                    url: privacyPolicy.url,
                    text: privacyPolicy.description,
                },
            ]}
        />
    );
};

/** Annual's copy is adapted from quarterly's reviewed wording, not itself legally reviewed — flag before shipping. */
export const PremiumUpgradeDisclosure = () => {
    const { data: investmentAccounts } = useGetApexAccounts();
    // The IM Client Agreement only applies to members with investment accounts.
    const hasInvestmentAccounts = (investmentAccounts?.length ?? 0) > 0;

    const eSignAgreement = POLICY_DISCLOSURES[PolicyType.E_SIGN_AGREEMENT];
    const depositAccountAgreement =
        POLICY_DISCLOSURES[PolicyType.DEPOSIT_ACCOUNT_AGREEMENT];
    const membershipAgreement =
        POLICY_DISCLOSURES[PolicyType.MEMBERSHIP_AGREEMENT];
    const investmentManagementAgreement =
        POLICY_DISCLOSURES[PolicyType.INVESTMENT_MANAGEMENT_AGREEMENT];

    const agreementsText = hasInvestmentAccounts
        ? '{ESignAgreementLink}, {DepositAccountAgreementLink}, {MembershipAgreementLink}, & {IMClientAgreementLink}'
        : '{ESignAgreementLink}, {DepositAccountAgreementLink}, & {MembershipAgreementLink}';

    return (
        <DisclosureTextWithLinks
            text={`By clicking Pay now, I understand this is a 12-month plan, and that I accept Fruitful's ${agreementsText}.`}
            links={[
                {
                    placeholder: '{ESignAgreementLink}',
                    url: eSignAgreement.url,
                    text: eSignAgreement.description,
                },
                {
                    placeholder: '{DepositAccountAgreementLink}',
                    url: depositAccountAgreement.url,
                    text: `Fruitful ${depositAccountAgreement.description}`,
                },
                {
                    placeholder: '{MembershipAgreementLink}',
                    url: membershipAgreement.url,
                    text: `Fruitful ${membershipAgreement.description}`,
                },
                ...(hasInvestmentAccounts
                    ? [
                          {
                              placeholder: '{IMClientAgreementLink}',
                              url: investmentManagementAgreement.url,
                              text: investmentManagementAgreement.description,
                          },
                      ]
                    : []),
            ]}
        />
    );
};

export const NewMemberInfoDisclosure = () => {
    const membershipAgreement =
        POLICY_DISCLOSURES[PolicyType.MEMBERSHIP_AGREEMENT];
    const premiumTrialMembershipTerms =
        POLICY_DISCLOSURES[PolicyType.PREMIUM_TRIAL_MEMBERSHIP_TERMS];
    const investmentManagementAgreement =
        POLICY_DISCLOSURES[PolicyType.INVESTMENT_MANAGEMENT_AGREEMENT];
    const formAdvBrochure =
        POLICY_DISCLOSURES[PolicyType.FORM_ADV_PART_2_BROCHURE];

    return (
        <DisclosureTextWithLinks
            text={
                'By clicking "Submit", you acknowledge you have reviewed and accept the terms of {MembershipAgreementLink} and {PremiumTrialMembershipTermsLink}, along with Fruitful Advisory, LLC\'s {InvestmentManagementAgreementLink} and {FormAdvLink}.'
            }
            links={[
                {
                    placeholder: '{MembershipAgreementLink}',
                    url: membershipAgreement.url,
                    text: `Fruitful's ${membershipAgreement.description}`,
                },
                {
                    placeholder: '{PremiumTrialMembershipTermsLink}',
                    url: premiumTrialMembershipTerms.url,
                    text: premiumTrialMembershipTerms.description,
                },
                {
                    placeholder: '{InvestmentManagementAgreementLink}',
                    url: investmentManagementAgreement.url,
                    text: investmentManagementAgreement.description,
                },
                {
                    placeholder: '{FormAdvLink}',
                    url: formAdvBrochure.url,
                    text: formAdvBrochure.description,
                },
            ]}
        />
    );
};

const DisclosureTextWithLink = ({
    text,
    linkPlaceholder,
    linkUrl,
    linkText,
}: {
    text: string;
    linkPlaceholder: string;
    linkUrl: Href;
    linkText: string;
}) => {
    // Expect only one existence of the placeholder. If additional, update
    // the validation and return values below.
    const textParts = text.split(linkPlaceholder);
    if (textParts.length !== 2) {
        captureWarning(
            'Unexpected placeholder in text. Exactly one placeholder expected.',
        );
    }

    return (
        <BodySmallRegular color="$gray1000">
            {textParts[0]}
            <Link href={linkUrl} target="_blank" rel="noopener noreferrer">
                <BodySmallRegular
                    color="$gray1000"
                    textDecorationLine="underline"
                >
                    {linkText}
                </BodySmallRegular>
            </Link>
            {textParts[1]}
        </BodySmallRegular>
    );
};

// Same idea as DisclosureTextWithLink above, generalized to more than one
// embedded link so the exact acknowledgement copy (with its own conjunctions,
// e.g. "along with") can be preserved instead of using the auto-joined list
// in BaseBankingPolicyDisclosures below.
// A single Text-like parent with inline Link children is the pattern that
// actually wraps on native (sibling Views each marked display="inline" don't).
const DisclosureTextWithLinks = ({
    text,
    links,
}: {
    text: string;
    links: Array<{ placeholder: string; url: Href; text: string }>;
}) => {
    let remainingText = text;
    const segments: React.ReactNode[] = [];

    links.forEach(({ placeholder, url, text: linkText }, index) => {
        const parts = remainingText.split(placeholder);
        if (parts.length !== 2) {
            captureWarning(
                'Unexpected placeholder in text. Exactly one placeholder expected.',
            );
        }
        segments.push(parts[0]);
        segments.push(
            <Link
                key={`link-${index}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
            >
                <BodySmallRegular
                    color="$gray1000"
                    textDecorationLine="underline"
                >
                    {linkText}
                </BodySmallRegular>
            </Link>,
        );
        remainingText = parts[1] ?? '';
    });

    segments.push(remainingText);

    return <BodySmallRegular color="$gray1000">{segments}</BodySmallRegular>;
};

export const BankingPolicyDisclosures = ({
    acknowledgementText,
    policies,
    onPress,
}: BankingPolicyDisclosuresProps) => {
    return BaseBankingPolicyDisclosures({
        acknowledgementText,
        policies,
        onPress,
        disclosures: POLICY_DISCLOSURES,
    });
};

export const CombinedBankingPolicyDisclosures = (
    props: BankingPolicyDisclosuresProps,
) => {
    return BaseBankingPolicyDisclosures({
        ...props,
        disclosures: POLICY_DISCLOSURES_BATCH_OPENING,
    });
};

const BaseBankingPolicyDisclosures = ({
    acknowledgementText,
    policies,
    onPress,
    disclosures,
    textSize = 'medium',
}: BaseBankingPolicyDisclosuresProps) => {
    const Text = textSize === 'small' ? BodySmallRegular : BodyMediumRegular;

    const disclosureMarkup = policies.map((policyType, i) => {
        let conjunction = '';
        if (i === policies.length - 2) {
            if (policies.length > 2) {
                conjunction = ', and ';
            } else {
                conjunction = ' and ';
            }
        } else if (i < policies.length - 2) {
            conjunction = ', ';
        }

        const disclosure = disclosures[policyType];
        return (
            <React.Fragment key={i}>
                <Link
                    href={disclosure.url}
                    target="_blank"
                    onPress={(e) => {
                        e.stopPropagation(); // prevents triggering the parent's onPress
                    }}
                >
                    <Text textDecorationLine="underline">
                        {disclosure.description}
                    </Text>
                </Link>
                <Text>{conjunction}</Text>
            </React.Fragment>
        );
    });

    return (
        <XStack
            flex={1}
            cursor={onPress != null ? 'pointer' : 'default'}
            onPress={onPress}
        >
            <Text>
                {acknowledgementText.trim()} {disclosureMarkup}
            </Text>
        </XStack>
    );
};

export const ProductFeatureDisclosures = ({
    activeDisclosures = [],
}: {
    activeDisclosures?: Array<'CASH' | 'CARD'>;
}) => {
    return (
        <YStack
            gap={12}
            paddingTop={48}
            paddingBottom={24}
            paddingHorizontal={8}
        >
            <LabelRegular color="$gray900">
                {`${BANKING_DISCLOSURE_SHORT_TEXT}  ${
                    FRUITFUL_CARD_DISCLOSURE
                }`}
            </LabelRegular>
            {activeDisclosures.map((type, index) => {
                const superscript = index + 1;
                switch (type) {
                    case 'CASH':
                        return (
                            <CashApyDisclosure
                                key={type}
                                superscript={superscript}
                                color="$gray900"
                                titleColor="$gray1000"
                            />
                        );
                    case 'CARD':
                        return (
                            <CashBackDisclosure
                                key={type}
                                superscript={superscript}
                                color="$gray900"
                                titleColor="$gray1000"
                            />
                        );
                    default: {
                        return null;
                    }
                }
            })}
        </YStack>
    );
};
